/**
 * useAppInstalled — vérifie si une app est installée ET active pour le commerce.
 * Utilisé pour gater l'accès aux modules comme Zifek Crédit.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface AppInstalledResult {
  installed: boolean;
  active: boolean;
  loading: boolean;
  appstoreId: number | null;
}

const cache: Record<string, AppInstalledResult> = {};

export function useAppInstalled(nompage: string): AppInstalledResult {
  const { user } = useAuth();
  const cacheKey = `${user?.id ?? 0}_${nompage}`;

  const [state, setState] = useState<AppInstalledResult>(
    cache[cacheKey] ?? { installed: false, active: false, loading: true, appstoreId: null }
  );

  const check = useCallback(async () => {
    if (!user) {
      setState({ installed: false, active: false, loading: false, appstoreId: null });
      return;
    }
    try {
      const [appRes, vendeurRes] = await Promise.all([
        supabase.from('appstore').select('id').eq('nompage', nompage).maybeSingle(),
        supabase.from('appvendeur').select('id, status').eq('idcommerce', user.id).eq('nompage', nompage).maybeSingle(),
      ]);
      const appstoreId = appRes.data?.id ?? null;
      const installed = !!vendeurRes.data;
      const active = vendeurRes.data?.status === 1;
      const result: AppInstalledResult = { installed, active, loading: false, appstoreId };
      cache[cacheKey] = result;
      setState(result);
    } catch {
      setState({ installed: false, active: false, loading: false, appstoreId: null });
    }
  }, [user, nompage, cacheKey]);

  useEffect(() => {
    check();
    const handler = () => { delete cache[cacheKey]; check(); };
    window.addEventListener('app-status-changed', handler);
    return () => window.removeEventListener('app-status-changed', handler);
  }, [check, cacheKey]);

  return state;
}